const LOG_PREFIX = '[StorageQuotaGuard]';
const PROTECTED_KEYS = new Set(['authConfigId']);
const PROTECTED_PREFIXES = ['oidc_key'];

const isBrowser = typeof window !== 'undefined' && typeof Storage !== 'undefined';

if (isBrowser) {
  const originalSetItem = Storage.prototype.setItem;

  Storage.prototype.setItem = function safeSetItem(key: string, value: string): void {
    try {
      originalSetItem.call(this, key, value);
    } catch (error) {
      if (!isQuotaExceededError(error)) {
        throw error;
      }

      console.warn(`${LOG_PREFIX} quota exceeded while writing "${key}". Clearing cached storage and retrying.`);
      const cleared = clearStorageExceptProtected(this);

      if (!cleared) {
        console.warn(`${LOG_PREFIX} failed to clear localStorage; skipped persisting "${key}".`);
        return;
      }

      try {
        originalSetItem.call(this, key, value);
      } catch (retryError) {
        if (isQuotaExceededError(retryError)) {
          console.warn(`${LOG_PREFIX} quota exceeded again after clearing; skipped persisting "${key}".`);
          return;
        }

        throw retryError;
      }
    }
  };
}

function clearStorageExceptProtected(storage: Storage): boolean {
  try {
    const preservedEntries = collectProtectedEntries(storage);
    storage.clear();
    preservedEntries.forEach(([key, storedValue]) => {
      storage.setItem(key, storedValue);
    });
    return true;
  } catch (error) {
    console.error(`${LOG_PREFIX} unexpected error while clearing localStorage`, error);
    return false;
  }
}

function collectProtectedEntries(storage: Storage): Array<[string, string]> {
  const protectedEntries: Array<[string, string]> = [];
  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index);
    if (!key || !isProtectedKey(key)) {
      continue;
    }

    const value = storage.getItem(key);
    if (value !== null) {
      protectedEntries.push([key, value]);
    }
  }
  return protectedEntries;
}

function isProtectedKey(key: string): boolean {
  return PROTECTED_KEYS.has(key) || PROTECTED_PREFIXES.some(prefix => key.startsWith(prefix));
}

function isQuotaExceededError(error: unknown): error is DOMException {
  return (
    error instanceof DOMException &&
    (error.code === 22 ||
      error.code === 1014 ||
      error.name === 'QuotaExceededError' ||
      error.name === 'NS_ERROR_DOM_QUOTA_REACHED')
  );
}
